import { type ComponentProps } from 'react';
import { type IconName, Icon as LaunchPadIcon } from '@launchpad-ui/icons';

// Custom Vital icon names based on the sprite
export type VitalIconName = 'calendar' | 'credit-card' | 'invoice';

type CustomIconProps = Omit<ComponentProps<'svg'>, 'children'> & {
  name: VitalIconName;
  size?: 'small' | 'medium' | 'large';
};

type IconProps = (ComponentProps<typeof LaunchPadIcon> & { name: IconName }) | CustomIconProps;

function isVitalIcon(name: string): name is VitalIconName {
  return ['calendar', 'credit-card', 'invoice'].includes(name);
}

function VitalIcon({ name, size = 'medium', className = '', ...props }: CustomIconProps) {
  const sizeMap = {
    small: 16,
    medium: 20,
    large: 24,
  };

  const iconSize = sizeMap[size];

  return (
    <svg width={iconSize} height={iconSize} className={`vital-icon ${className}`} {...props}>
      <use href={`#app-icon-${name}`} />
    </svg>
  );
}

export function Icon({ name, ...props }: IconProps) {
  if (isVitalIcon(name)) {
    const { name: unusedName, ...restProps } = props as CustomIconProps;
    return <VitalIcon name={name} {...restProps} />;
  }

  const { name: unusedName, ...restProps } = props as ComponentProps<typeof LaunchPadIcon>;
  return <LaunchPadIcon name={name as IconName} {...restProps} />;
}

export type { IconName };
